/*---------------------------------------------------------------
 ?? Project: Ring2All Platform
 ????? Author: Rodrigo Cuadra
 ?? Company: Ring2All LLC
 ?? File: components\BackupHistoryBox.tsx
 ??? Created: 2026-05-02
 ?? Description:
    Implementation of the maintenance module.
----------------------------------------------------------------*/


import { RefreshCw, Download, Trash2 } from 'lucide-react';
import { FormBox } from '../../../../components/ui/FormBox';
import SimpleTooltip from '../../../../components/ui/SimpleTooltip';
import { useI18n } from '../../../../hooks/useI18n';

interface BackupEntry { filename: string; createdAt: string; size: number; }

interface Props {
    backups: BackupEntry[];
    isLoading: boolean;
    onDownload: (filename: string) => void;
    onDelete: (filename: string) => void;
}

export function BackupHistoryBox({ backups, isLoading, onDownload, onDelete }: Props) {
    const { t } = useI18n();

    return (
        <FormBox
            title={t('backupRestore:sections.backupsList')}
            description={t('backupRestore:sections.backupsListDesc', 'History of backups created by this job')}
            icon={<RefreshCw className="h-5 w-5 text-primary" />}
        >
            <div className="overflow-x-auto">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-muted-foreground uppercase bg-muted/50">
                        <tr>
                            <th className="px-4 py-3 font-medium">
                                <SimpleTooltip content={t('backupRestore:tooltips.backupFilename')}>{t('backupRestore:columns.filename')}</SimpleTooltip>
                            </th>
                            <th className="px-4 py-3 font-medium">
                                <SimpleTooltip content={t('backupRestore:tooltips.date')}>{t('backupRestore:columns.date')}</SimpleTooltip>
                            </th>
                            <th className="px-4 py-3 font-medium">
                                <SimpleTooltip content={t('backupRestore:tooltips.size')}>{t('backupRestore:columns.size')}</SimpleTooltip>
                            </th>
                            <th className="px-4 py-3 font-medium text-right">{t('backupRestore:columns.actions')}</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y divide-border">
                        {isLoading ? (
                            <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">Loading backups...</td></tr>
                        ) : backups.length === 0 ? (
                            <tr><td colSpan={4} className="px-4 py-8 text-center text-muted-foreground">No backups found</td></tr>
                        ) : backups.map((item, idx) => (
                            <tr key={idx} className="hover:bg-muted/30 transition-colors">
                                <td className="px-4 py-3 font-medium">{item.filename}</td>
                                <td className="px-4 py-3 text-muted-foreground">{new Date(item.createdAt).toLocaleString()}</td>
                                <td className="px-4 py-3 text-muted-foreground">{(item.size / 1024 / 1024).toFixed(2)} MB</td>
                                <td className="px-4 py-3 text-right">
                                    <div className="flex items-center justify-end gap-2">
                                        <SimpleTooltip content={t('backupRestore:tooltips.downloadBackup')}>
                                            <button onClick={() => onDownload(item.filename)} className="p-1.5 hover:bg-blue-100 dark:hover:bg-blue-900/30 rounded-md transition-colors text-blue-600 dark:text-blue-400">
                                                <Download className="h-4 w-4" />
                                            </button>
                                        </SimpleTooltip>
                                        <SimpleTooltip content={t('backupRestore:tooltips.deleteBackup')}>
                                            <button onClick={() => onDelete(item.filename)} className="p-1.5 hover:bg-red-100 dark:hover:bg-red-900/30 rounded-md transition-colors text-red-600 dark:text-red-400">
                                                <Trash2 className="h-4 w-4" />
                                            </button>
                                        </SimpleTooltip>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </FormBox>
    );
}
